// Pure functions for groups feature — no side effects, fully testable

/**
 * Parse a URL path into a group route descriptor.
 * Returns null for non-group paths.
 * @param {string} path - window.location.pathname
 * @returns {{ view: string, params: Object } | null}
 */
export function parseGroupRoute(path) {
  if (path === '/groups') return { view: 'groups', params: {} };

  if (path.startsWith('/groups/join/')) {
    const parts = path.split('/').filter(Boolean);
    // Expect: ['groups', 'join', groupId, token]
    if (parts.length === 4) {
      return { view: 'group-join', params: { groupId: parts[2], token: parts[3] } };
    }
    return null;
  }

  if (path.startsWith('/groups/')) {
    const groupId = path.slice('/groups/'.length);
    if (groupId) return { view: 'group-detail', params: { groupId } };
  }

  return null;
}

/**
 * Normalize a binder URL to a valid Firestore document key.
 * Strips leading slash and replaces dots/slashes with underscores.
 * Example: '/mfb.abc123' → 'mfb_abc123'
 * @param {string} url
 * @returns {string}
 */
export function normalizeBinderKey(url) {
  if (!url) return '';
  return url.replace(/^\//, '').replace(/[./]/g, '_');
}

/**
 * Build the full short invite URL from a shortCode.
 * @param {string} shortCode - 8-char short code from invite_codes collection
 * @param {string} [origin] - Defaults to window.location.origin
 * @returns {string}
 */
export function buildInviteUrl(
  shortCode,
  origin = typeof window !== 'undefined' ? window.location.origin : ''
) {
  return `${origin}/invite/${shortCode}`;
}

/**
 * Check if an invite has expired.
 * @param {Date | string | number | { toDate: () => Date } | null | undefined} expiresAt
 *   Firestore Timestamp, Date, or anything `new Date()` accepts
 * @returns {boolean}
 */
export function isInviteExpired(expiresAt) {
  if (!expiresAt) return false;
  const expireDate =
    typeof expiresAt === 'object' && 'toDate' in expiresAt
      ? expiresAt.toDate()
      : new Date(expiresAt);
  return expireDate < new Date();
}

/**
 * Check if an actor with a given role can take action against a target member.
 * Owner can target anyone. Admin can only target regular members.
 * @param {string | null} actorRole - a GroupRole; anything else can act on nobody
 * @param {string | null} targetRole - a GroupRole; anything else is untouchable
 * @returns {boolean}
 */
export function canActorTargetMember(actorRole, targetRole) {
  if (actorRole === 'owner') return true;
  if (actorRole === 'admin') return targetRole === 'member';
  return false;
}

// ============================================================
// Group Store Helpers (extracted from groups.svelte.js)
// ============================================================

/**
 * Resolve a UID to a display name from a lookup map.
 * Returns the UID itself as fallback if no name is found.
 * @param {Object.<string, string>} uidToNameMap
 * @param {string} uid
 * @returns {string}
 */
export function resolveDisplayNameFromMap(uidToNameMap, uid) {
  return uidToNameMap[uid] || uid;
}

/**
 * Check if the current user is a member of a specific group.
 * @param {Array<{ groupId: string }>} userGroups
 * @param {string} groupId
 * @returns {boolean}
 */
export function isUserInGroupList(userGroups, groupId) {
  return userGroups.some((g) => g.groupId === groupId);
}

/**
 * Get the current user's role in a specific group.
 * @param {Array<Pick<import('../types.js').UserGroup, 'groupId' | 'role'>>} userGroups
 * @param {string} groupId
 * @returns {import('../types.js').GroupRole | null} null when not a member
 */
export function getUserRoleInGroupList(userGroups, groupId) {
  const group = userGroups.find((g) => g.groupId === groupId);
  return group?.role ?? null;
}

/**
 * One avatar badge: the photo when there is one, else the name's initial on a
 * color hashed from `id`, so the same person (or group) looks the same
 * everywhere.
 * @param {string} id - UID (or any stable id) the color is hashed from.
 * @param {string | null | undefined} name
 * @param {string | null | undefined} photoUrl
 * @returns {{ uid: string, name: string, photoUrl: string|null, initial: string, color: string }}
 */
export function avatarBadge(id, name, photoUrl) {
  const label = name || '?';
  // Hash id for consistent color (same algorithm as before for visual stability).
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return {
    uid: id,
    name: label,
    photoUrl: photoUrl || null,
    initial: label.charAt(0).toUpperCase(),
    color: `hsl(${hue}, 35%, 48%)`,
  };
}

/**
 * Build avatar badges for a group card preview: a small list of members rendered
 * as a photo when available, or a colored initial as fallback.
 *
 * Filters out falsy UIDs defensively — `loadUserGroups` derives them from
 * `m.uid || m.id` which can yield undefined if a member record is missing both.
 *
 * @param {string[]} uids - Member UIDs for the group.
 * @param {Object.<string, string>} uidToName - UID → display name map.
 * @param {Object.<string, string|null>} uidToPhoto - UID → photo URL map.
 * @param {number} [limit=5] - Max badges to return.
 * @returns {Array<{ uid: string, name: string, photoUrl: string|null, initial: string, color: string }>}
 */
export function buildMemberAvatarBadges(uids, uidToName, uidToPhoto, limit = 5) {
  return (uids || [])
    .filter(Boolean)
    .slice(0, limit)
    .map((uid) => avatarBadge(uid, uidToName?.[uid], uidToPhoto?.[uid]));
}

/**
 * Calculate an invite expiry date from a number of days.
 * @param {number|null} expiresInDays - Days until expiry, or null/0 for no expiry
 * @param {Date} [now] - Current date (for testability)
 * @returns {Date|null}
 */
export function calculateInviteExpiry(expiresInDays, now = new Date()) {
  if (!expiresInDays) return null;
  const d = new Date(now);
  d.setDate(d.getDate() + expiresInDays);
  return d;
}

// ============================================================
// Subgroup selection (web subgroup picker spec)
// ============================================================

/**
 * The subset of `UserGroup` these helpers need — narrower than the full type
 * so callers (and these tests) don't have to carry `role`/etc. just to
 * describe a subgroup list.
 * @typedef {{name: string, groupId?: string, subgroups?: import('../types.js').Subgroup[]}} SubgroupHost
 */

/** @param {SubgroupHost} group */
function subgroupsOf(group) {
  return Array.isArray(group?.subgroups) ? group.subgroups : [];
}

/**
 * The viewer's own subgroups in this server — the picker's default and the
 * bot's Effective Reach.
 * @param {SubgroupHost} group
 * @returns {string[]}
 */
export function defaultSubgroupSelection(group) {
  return subgroupsOf(group)
    .filter((s) => s.isMember)
    .map((s) => s.groupId);
}

/**
 * What actually gets sent/shown: [] when nothing or everything is checked
 * (both mean the whole server), otherwise the checked ids in the server's
 * own subgroup order, unknown ids dropped.
 * @param {SubgroupHost} group
 * @param {string[]} ids
 * @returns {string[]}
 */
export function effectiveSubgroupSelection(group, ids) {
  const all = subgroupsOf(group).map((s) => s.groupId);
  if (all.length === 0) return [];
  const set = new Set(ids || []);
  const picked = all.filter((id) => set.has(id));
  return picked.length === 0 || picked.length === all.length ? [] : picked;
}

/**
 * @param {SubgroupHost} group
 * @param {string[]} ids
 * @returns {string}
 */
export function subgroupPickerLabel(group, ids) {
  const picked = effectiveSubgroupSelection(group, ids);
  if (picked.length === 0) return group.name;
  return `${group.name} · ${picked.length} of ${subgroupsOf(group).length}`;
}

/** @param {string | undefined | null} raw */
export function parseSubParam(raw) {
  if (!raw) return [];
  const out = [];
  for (const part of String(raw).split(',')) {
    const id = part.trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** @param {string[]} ids */
export function formatSubParam(ids) {
  return (ids || []).join(',');
}

/**
 * Groups-page card line: "3 groups: Brooklyn, Queens, Manhattan".
 * @param {SubgroupHost} group
 * @returns {string}
 */
export function subgroupSummary(group) {
  const subs = subgroupsOf(group);
  if (subs.length === 0) return '';
  const noun = subs.length === 1 ? 'group' : 'groups';
  return `${subs.length} ${noun}: ${subs.map((s) => s.label).join(', ')}`;
}
